import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface RatingBadgeProps {
  rating: number;
  deviation?: number;
  showTrend?: boolean;
  ratingDelta?: number;
  showTooltip?: boolean;
}

export function RatingBadge({ 
  rating, 
  deviation, 
  showTrend = false, 
  ratingDelta = 0,
  showTooltip = true 
}: RatingBadgeProps) {
  const getLevel = (rating: number) => {
    if (rating >= 2000) return { 
      level: 'Expert', 
      variant: 'default' as const, 
      color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30' 
    };
    if (rating >= 1700) return { 
      level: 'Advanced', 
      variant: 'secondary' as const, 
      color: 'bg-blue-500/10 text-blue-600 border-blue-500/30' 
    };
    if (rating >= 1400) return { 
      level: 'Intermediate', 
      variant: 'outline' as const, 
      color: 'bg-green-500/10 text-green-600 border-green-500/30' 
    };
    return { 
      level: 'Beginner', 
      variant: 'outline' as const, 
      color: 'bg-muted text-muted-foreground' 
    };
  };

  const { level, variant, color } = getLevel(rating);
  const formattedRating = Math.round(rating);
  const confidence = deviation ? Math.round(1.96 * deviation) : null;

  const getTrendIcon = () => {
    if (!showTrend || Math.abs(ratingDelta) < 1) return <Minus className="h-3 w-3" />;
    if (ratingDelta > 0) return <TrendingUp className="h-3 w-3 text-emerald-600" />;
    return <TrendingDown className="h-3 w-3 text-rose-600" />;
  };

  const tooltipContent = (
    <div className="space-y-1.5 text-xs">
      <div className="font-semibold">{level} Level</div>
      <div>Rating: {formattedRating}</div>
      {confidence && (
        <div className="text-muted-foreground">
          Confidence: ±{confidence} (95%)
        </div>
      )}
      {showTrend && Math.abs(ratingDelta) >= 1 && (
        <div className={ratingDelta > 0 ? 'text-emerald-600' : 'text-rose-600'}>
          Recent: {ratingDelta > 0 ? '+' : ''}{Math.round(ratingDelta)}
        </div>
      )}
      <div className="text-muted-foreground pt-1 border-t border-border/50">
        Based on match performance
      </div>
    </div>
  );

  const content = (
    <div className="flex items-center gap-2">
      <Badge variant={variant} className={color}>
        {level}
      </Badge>
      <span className="font-mono text-sm font-semibold">
        {formattedRating}
        {confidence && (
          <span className="text-muted-foreground font-normal ml-1 text-xs">
            ±{confidence}
          </span>
        )}
      </span>
      {showTrend && (
        <span className="ml-1">
          {getTrendIcon()}
        </span>
      )}
    </div>
  );

  if (!showTooltip) return content;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">
            {content}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}